import { useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Theme, css, useTheme } from '@emotion/react';
import { Plus, RocketLaunch } from '@phosphor-icons/react';
import { getPlaceholderUrl } from 'common/commonUtil';
import { useEditAppContext } from 'client/lib/EditApp';
import { useAccount } from 'client/lib/User';
import { useProjectState } from 'client/state/ProjectState';
import type { BreadcrumbsItemProps } from 'client/components/Breadcrumbs';
import { Button } from 'client/components/Button';
import { Skeleton } from 'client/components/Skeleton';
import { Subheader } from 'client/components/Subheader';

const folderIconSVG = (theme: Theme) =>
  `data:image/svg+xml,%3Csvg width='40' height='40' fill='none' ` +
  `style='background: ${theme.color.grey[60].replace('#', '%23')}' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath ` +
  "d='M29 16.3v10.5a.7.7 0 0 1-.7.7H11.7a.7.7 0 0 1-.7-.8V14a.7.7 0 0 1 .8-.8h5l.4.2 2.6 2 .5.1h8a.7.7 0 0 1 .7.8Z' " +
  `stroke='${theme.color.grey[80].replace('#', '%23')}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E`;

export const ProjectSubheader = () => {
  const theme = useTheme();
  const params = useParams();
  const { pathname } = useLocation();
  const account = useAccount();

  const projectID = parseInt(params.projectID || '0');
  const name = useProjectState((state) => state.document?.name || '');
  const isQuickLaunch = useProjectState((state) => state.document?.repositoryUrl == null);
  const editAppContext = useEditAppContext();

  const activeSection = (pathname.split('/').length === 5 && pathname.split('/').pop()) || 'deployments';

  const imageUrl = getPlaceholderUrl('project', projectID);

  const projectUrl = `/${account?.identifier}/project/${projectID}`;

  const tabsProps = useMemo(
    () => ({
      items: [
        {
          id: 'deployments',
          title: 'Deployments',
          href: projectUrl,
        },
        {
          id: 'options',
          title: 'Options',
          href: `${projectUrl}/options`,
        },
      ],
      selected: activeSection,
    }),
    [projectUrl, activeSection]
  );

  const breadcrumbs = useMemo<BreadcrumbsItemProps[]>(
    () => [
      {
        title: 'Projects',
        href: '../../projects',
      },
      {
        title: name,
        image: { alt: 'project image', src: imageUrl || '', shape: 'hexagon' },
        disabled: true,
      },
    ],
    [name, imageUrl]
  );

  const isLoading = !editAppContext.isReady;

  let title: JSX.Element | string = <Skeleton width={300} />;
  if (!isLoading && name) title = name;
  if (!isLoading && name && isQuickLaunch) {
    title = (
      <>
        <RocketLaunch weight="duotone" css={quickLaunchIconStyle} />
        <span style={{ display: 'inline-block', width: '1.2em', height: '1ch' }} />
        {name}
      </>
    );
  }

  return (
    <Subheader
      title={title}
      image={{ alt: 'project logo', src: imageUrl || folderIconSVG(theme), shape: 'hexagon' }}
      type="Project"
      breadcrumbs={breadcrumbs}
      // alertBar={<CoreAlertBar items={coreAlertBarItems as Exclude<typeof coreAlertBarItems[number], null>[]} />}
      actions={
        <Button icon={<Plus weight="bold" />} href={`${projectUrl}/deployment/create`} primary>
          Create Deployment
        </Button>
      }
      tabs={tabsProps}
    />
  );
};

const quickLaunchIconStyle = css`
  position: absolute;
  fill: url(#gradient-one);
  transform: translateY(-3px);
`;
